// Inline widgets in an answer's text (ECONIC answers as prose with inline widgets, 17 Oct 2026). The server and the client share one
// contract: a widget is placed by the marker `[[w:<blockId>]]` alone on its own line (a paragraph by itself), right after the sentence it
// supports. A message carries `extras.layout = "inline-1"` when its text places its widgets this way; every other message keeps the earlier
// layout. These helpers are pure (no React): the marker grammar, the ids a text places, the text without markers (copy, previews, any
// surface that draws no widget) and, while an answer streams, the text without a marker or a citation that has not finished arriving.

/** The id grammar of a widget marker (the server's `InlineWidgets` uses the same). */
const ID = "[A-Za-z0-9][A-Za-z0-9:_\\-.]{0,79}";

/** A marker anywhere in a text (global). */
export const WIDGET_MARKER = new RegExp(`\\[\\[w:(${ID})\\]\\]`, "g");

/** A line that is a marker and nothing else (multiline, global): the only place a widget is drawn. */
export const WIDGET_MARKER_LINE = new RegExp(`^\\s*\\[\\[w:(${ID})\\]\\]\\s*$`, "gm");

const LONE = new RegExp(`^\\s*\\[\\[w:(${ID})\\]\\]\\s*$`);

/** The layout value of a message whose text places its widgets with markers. */
export const INLINE_LAYOUT = "inline-1";

/** What a host is asked to draw for a placed marker. */
export interface InlineWidgetRef {
  /** The block id the marker names. */
  id: string;
  /** The answer is still being written (a block not yet arrived may show its placeholder). */
  streaming: boolean;
}

/** The id of a line that is a marker alone, else null. */
export function markerOfLine(line: string): string | null {
  const m = LONE.exec(line);
  return m ? m[1]! : null;
}

const FENCE = /^\s{0,3}(`{3,}|~{3,})/;

/** Walks a markdown text line by line, telling whether each line sits inside a fenced code block. */
function eachLine(text: string, fn: (line: string, inCode: boolean, index: number, lines: string[]) => void): string[] {
  const lines = text.split("\n");
  let fence: string | null = null;
  lines.forEach((line, i) => {
    const f = FENCE.exec(line);
    if (fence) {
      fn(line, true, i, lines);
      if (f && f[1]![0] === fence[0] && f[1]!.length >= fence.length && line.trim() === f[1]) fence = null;
      return;
    }
    if (f) {
      fence = f[1]!;
      fn(line, true, i, lines);
      return;
    }
    fn(line, false, i, lines);
  });
  return lines;
}

/** The ids the text places (markers alone on their line, outside code), in order, each once. */
export function widgetIdsIn(text: string | null | undefined): string[] {
  if (!text || !text.includes("[[w:")) return [];
  const out: string[] = [];
  eachLine(text, (line, inCode) => {
    if (inCode) return;
    const id = markerOfLine(line);
    if (id && !out.includes(id)) out.push(id);
  });
  return out;
}

/** Whether a text carries any widget marker. */
export function hasWidgetMarkers(text: string | null | undefined): boolean {
  return !!text && text.includes("[[w:") && new RegExp(WIDGET_MARKER.source).test(text);
}

/**
 * The text without its markers, for every reader that draws no widget (copy as Markdown, previews, notifications): a marker line goes with
 * its blank line, a marker inside a sentence goes alone, and no run of blank lines is left behind. Code keeps what it shows.
 */
export function stripWidgetMarkers(text: string): string;
export function stripWidgetMarkers(text: string | null | undefined): string | null | undefined;
export function stripWidgetMarkers(text: string | null | undefined): string | null | undefined {
  if (!text || !text.includes("[[w:")) return text;
  const kept: string[] = [];
  eachLine(text, (line, inCode) => {
    if (inCode) { kept.push(line); return; }
    if (markerOfLine(line)) return;
    kept.push(line.replace(WIDGET_MARKER, "").replace(/[ \t]{2,}/g, " ").replace(/[ \t]+$/, ""));
  });
  return kept.join("\n").replace(/\n{3,}/g, "\n\n").replace(/^(?:[ \t]*\n)+/, "").replace(/(?:\n[ \t]*)+$/, "");
}

type Ctx = "none" | "para" | "list" | "table";
type LineKind = "para" | "heading" | "rule" | "table" | "item" | "continuation";

const HEADING_LINE = /^\s{0,3}#{1,6}\s/;
const RULE_LINE = /^\s{0,3}([-*_])(\s*\1){2,}\s*$/;
const TABLE_LINE = /^\s*\|/;
const ITEM_LINE = /^\s*([-*+]|\d{1,3}[.)])\s+/;
const INDENTED_LINE = /^\s{2,}\S/;
const BLANK_RUN = /\n[ \t]*\n(?:[ \t]*\n)+/g;
const SPACE_RUN = /(?<=\S)[ \t]{2,}(?=\S)/g;

function classify(line: string, ctx: Ctx, blankSince: boolean): LineKind {
  if (HEADING_LINE.test(line)) return "heading";
  if (RULE_LINE.test(line)) return "rule";
  if (TABLE_LINE.test(line)) return "table";
  if (ITEM_LINE.test(line)) return "item";
  if (ctx === "list" && (!blankSince || INDENTED_LINE.test(line))) return "continuation";
  return "para";
}

/**
 * The text with its markers where the server's `InlineWidgets.Compose` puts them (the same rules, line for line): each kept marker alone in
 * its own paragraph, unindented, after the paragraph, list or table it was written in (a marker inside a sentence, a list item, a table
 * cell or under a heading is moved after it); a marker `accept` refuses is removed; code is left as written. `accept` is asked once per
 * marker, in text order (the SDK's acceptor applies the server's checks: a known block, placed once, at most two besides drafts). While an
 * answer streams this keeps the text drawn as the saved answer will be, so nothing appears, disappears or moves when it is saved; on a saved
 * answer (the server's output) it changes nothing.
 */
export function normaliseWidgetMarkers(text: string, accept: (id: string) => boolean = () => true): string {
  if (!text || !text.includes("[[w:")) return text;
  const output: string[] = [];
  const pending: string[] = [];
  let ctx: Ctx = "none";
  let blankSince = false;
  let fence: string | null = null;
  const flush = () => {
    if (pending.length === 0) return;
    for (const id of pending) output.push("", `[[w:${id}]]`);
    output.push("");
    pending.length = 0;
  };
  for (const raw of text.replace(/\r\n/g, "\n").split("\n")) {
    const line = raw.trimEnd();
    const lead = line.trimStart();
    if (fence !== null) {
      output.push(raw);
      if (lead.startsWith(fence)) fence = null;
      continue;
    }
    if (lead.startsWith("```") || lead.startsWith("~~~")) {
      if (!(ctx === "list" && line.length - lead.length >= 2)) { flush(); ctx = "none"; }
      fence = lead.slice(0, 3);
      output.push(raw);
      continue;
    }
    const ids: string[] = [];
    let hadMarker = false;
    let rest = line.replace(WIDGET_MARKER, (_m, id: string) => {
      hadMarker = true;
      if (accept(id)) ids.push(id);
      return "";
    });
    if (hadMarker) rest = rest.replace(SPACE_RUN, " ").trimEnd();
    if (rest.trim().length === 0) {
      if (!hadMarker) {
        // A blank line ends a paragraph and a table; a list may go on after it.
        if (ctx === "para" || ctx === "table") { flush(); ctx = "none"; }
        else if (ctx === "list") blankSince = true;
        output.push("");
        continue;
      }
      pending.push(...ids);
      if (ctx !== "list" && ctx !== "table") { flush(); ctx = "none"; }
      continue;
    }
    const kind = classify(rest, ctx, blankSince);
    const breaks = ctx === "para" ? kind === "heading" || kind === "rule" || kind === "table" || kind === "item"
      : ctx === "list" ? kind !== "item" && kind !== "continuation"
      : ctx === "table" ? kind !== "table"
      : false;
    if (breaks) flush();
    output.push(hadMarker ? rest : raw);
    if (kind === "heading" || kind === "rule") ctx = "none";
    else if (kind === "item" || kind === "continuation") { ctx = "list"; blankSince = false; }
    else if (kind === "table") { ctx = "table"; blankSince = false; }
    else ctx = "para";
    pending.push(...ids);
    if (ctx === "none") flush();
  }
  flush();
  return output.join("\n").replace(BLANK_RUN, "\n\n").trim();
}

/** Every marker in its own paragraph, unindented, after the paragraph, list or table it was written in; code untouched (all kept). */
export function isolateWidgetMarkers(text: string): string {
  return normaliseWidgetMarkers(text);
}

/** A trailing marker not yet closed: "[", "[[", "[[w", "[[w:fig-c", "[[w:fig-cpi]". */
const PARTIAL_WIDGET = new RegExp(`\\[(?:\\[(?:w(?::[A-Za-z0-9:_\\-.]{0,80}\\]?)?)?)?$`);
/** A trailing citation not yet closed: "[S", "[S1", "[S1, S", "[S1,". */
const PARTIAL_CITE = /\[\s*S\d{0,3}(?:\s*[,،;]\s*S?\d{0,3})*\s*$/;

/**
 * While an answer streams: the text without a marker or a citation still arriving at its end, so neither shows as raw text for a moment.
 * Only the very end of the text is looked at; a finished answer is never passed through this.
 */
export function hidePartial(text: string): string {
  if (!text) return text;
  const tail = text.slice(-100);
  if (!tail.includes("[")) return text;
  const w = PARTIAL_WIDGET.exec(tail);
  if (w) return text.slice(0, text.length - w[0].length);
  const c = PARTIAL_CITE.exec(tail);
  if (c) return text.slice(0, text.length - c[0].length);
  return text;
}
